// ============================================================================
// CHANGO V5.0 — D6.6 Ops: Email-replies admin list
// ============================================================================
// Admin-only. Surfaces the `email_replies` table so ops can:
//   - See inbound replies that succeeded ("posted" — linked to a comment)
//   - See replies stuck in "received" (auto-post failed or sender wasn't
//     in the workspace) and either retry the auto-post or manually ignore
//   - Filter by status and sender-domain
//
// Backed by /api/ops/mail-inbound:
//   GET  /                       list rows (status filter server-side)
//   POST /:id/retry-post         re-run autoPostInboundReply
//   POST /:id/ignore  { note }   mark manually ignored
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useAuth } from '../../hooks/useAuth.js';
import { api, HttpError } from '../../api/client.js';
import {
  STATUS_META,
  matchesDomainFilter,
} from '../../lib/mailInboundFilter.js';

function formatTs(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('zh-CN', { hour12: false });
  } catch {
    return iso;
  }
}

function StatusPill({ status }) {
  const m = STATUS_META[status] || { label: status || '—', tone: 'bg-slate-100 text-slate-700' };
  return (
    <span className={['inline-block rounded px-2 py-0.5 text-xs font-medium', m.tone].join(' ')}>
      {m.label}
    </span>
  );
}

function IgnoreDialog({ open, onClose, onSubmit, busy }) {
  const [note, setNote] = useState('');
  useEffect(() => { if (open) setNote(''); }, [open]);
  if (!open) return null;

  const trimmed = note.trim();
  const lenOk = trimmed.length >= 4 && trimmed.length <= 500;

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-slate-900/60" role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
        <h3 className="text-base font-semibold text-slate-800">手动忽略</h3>
        <p className="mt-1 text-xs text-slate-500">
          请说明忽略原因（写入 <code>email_replies.status_note</code>，至少 4 个字符）。
        </p>
        <textarea
          className="mt-3 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-chango-500 focus:outline-none"
          rows={4}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="示例：自动回复 / 垃圾邮件 / 已在别处处理"
          maxLength={500}
        />
        <div className="mt-1 flex justify-between text-xs text-slate-400">
          <span>{trimmed.length}/500</span>
          {!lenOk && trimmed.length > 0 && (
            <span className="text-rose-500">至少 4 个字符</span>
          )}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            取消
          </button>
          <button
            type="button"
            onClick={() => onSubmit(trimmed)}
            disabled={!lenOk || busy}
            className="rounded-md bg-slate-700 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
          >
            {busy ? '处理中…' : '确认忽略'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReplyRow({ row, onAction, busy }) {
  const [expanded, setExpanded] = useState(false);
  const canRetry = row.status === 'received';
  const canIgnore = row.status === 'received';

  return (
    <>
      <tr className="border-t border-slate-100 hover:bg-slate-50/50">
        <td className="px-4 py-3 font-mono text-xs">#{row.id}</td>
        <td className="px-4 py-3 font-mono text-xs text-slate-500">#{row.outbox_id}</td>
        <td className="px-4 py-3 font-mono text-xs text-slate-600">{row.from_email || '—'}</td>
        <td className="px-4 py-3 text-slate-700">{row.subject || '（无主题）'}</td>
        <td className="px-4 py-3"><StatusPill status={row.status} /></td>
        <td className="px-4 py-3 text-xs text-slate-500">{row.status_note || '—'}</td>
        <td className="px-4 py-3 text-slate-500">{formatTs(row.received_at)}</td>
        <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
          {canRetry && (
            <button
              type="button"
              onClick={() => onAction('retry', row)}
              disabled={busy}
              className="rounded bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
              title="重试：再次尝试将此回复自动发布为评论"
            >
              重试
            </button>
          )}
          {canIgnore && (
            <button
              type="button"
              onClick={() => onAction('ignore', row)}
              disabled={busy}
              className="rounded border border-slate-300 px-3 py-1 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              忽略
            </button>
          )}
          <button
            type="button"
            onClick={() => setExpanded((x) => !x)}
            className="rounded px-2 py-1 text-xs text-slate-500 hover:text-slate-800"
          >
            {expanded ? '收起' : '详情'}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-slate-50/70">
          <td colSpan={8} className="px-4 py-3">
            <pre className="max-h-64 overflow-auto rounded border border-slate-200 bg-white p-3 text-xs text-slate-700">
{JSON.stringify(row, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

function useMailReplies(filters) {
  const [state, setState] = useState({ rows: [], loading: true, error: null });
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((x) => x + 1), []);

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    const query = new URLSearchParams();
    if (filters.status) query.set('status', filters.status);
    query.set('limit', '200');

    (async () => {
      try {
        const res = await api.get(`/api/ops/mail-inbound?${query.toString()}`);
        if (cancelled) return;
        setState({
          rows: Array.isArray(res?.items) ? res.items : [],
          loading: false,
          error: null,
        });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof HttpError && err.status === 404) {
          setState({ rows: [], loading: false, error: null });
        } else {
          setState({ rows: [], loading: false, error: err.message || '加载失败' });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [filters.status, reloadKey]);

  return { ...state, reload };
}

export default function OpsMailInbound() {
  const { user } = useAuth();
  const [statusFilter, setStatusFilter] = useState('received');
  const [domainFilter, setDomainFilter] = useState('');
  const filters = useMemo(() => ({ status: statusFilter }), [statusFilter]);
  const { rows, loading, error, reload } = useMailReplies(filters);

  const [busyId, setBusyId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [ignoreRow, setIgnoreRow] = useState(null);

  const filteredRows = useMemo(
    () => rows.filter((r) => matchesDomainFilter(r, domainFilter)),
    [rows, domainFilter]
  );

  const onAction = async (kind, row) => {
    setActionError(null);
    if (kind === 'ignore') {
      setIgnoreRow(row);
      return;
    }
    setBusyId(row.id);
    try {
      if (kind === 'retry') {
        const res = await api.post(`/api/ops/mail-inbound/${row.id}/retry-post`, {});
        if (res && res.outcome === 'error') {
          throw new Error(res.reason || '重试失败');
        }
      }
      reload();
    } catch (err) {
      const detail = err instanceof HttpError
        ? `${err.code}: ${err.message}`
        : (err.message || '操作失败');
      setActionError(detail);
    } finally {
      setBusyId(null);
    }
  };

  const submitIgnore = async (note) => {
    if (!ignoreRow) return;
    setBusyId(ignoreRow.id);
    setActionError(null);
    try {
      await api.post(`/api/ops/mail-inbound/${ignoreRow.id}/ignore`, { note });
      setIgnoreRow(null);
      reload();
    } catch (err) {
      setActionError(err.message || '忽略失败');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100">
      <header className="border-b border-slate-700 bg-slate-800/80">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div>
            <h1 className="text-lg font-semibold">邮件回复收件箱</h1>
            <p className="text-xs text-slate-400">
              {user?.email} · {user?.role} · 自动发布失败的回复会停留在「待处理」队列
            </p>
          </div>
          <button
            type="button"
            onClick={reload}
            className="rounded border border-slate-600 px-3 py-1 text-xs text-slate-200 hover:bg-slate-700"
          >
            刷新
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded border border-slate-600 bg-slate-800 px-3 py-2 text-sm"
          >
            <option value="">全部状态</option>
            {Object.entries(STATUS_META).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          <input
            type="text"
            value={domainFilter}
            onChange={(e) => setDomainFilter(e.target.value)}
            placeholder="按发件域过滤（例：example.com）"
            className="rounded border border-slate-600 bg-slate-800 px-3 py-2 text-sm w-72"
          />
          <span className="ml-auto text-xs text-slate-400">
            共 {filteredRows.length}{domainFilter ? ` / ${rows.length}` : ''} 条
          </span>
        </div>

        {(error || actionError) && (
          <div className="mb-4 rounded-md border border-rose-500/40 bg-rose-900/30 px-4 py-2 text-sm text-rose-200">
            {error || actionError}
          </div>
        )}

        <div className="overflow-hidden rounded-lg border border-slate-700 bg-slate-800/40">
          <table className="w-full text-sm">
            <thead className="bg-slate-800/80 text-left text-xs font-medium uppercase tracking-wide text-slate-400">
              <tr>
                <th className="px-4 py-3">编号</th>
                <th className="px-4 py-3">原始邮件</th>
                <th className="px-4 py-3">发件人</th>
                <th className="px-4 py-3">主题</th>
                <th className="px-4 py-3">状态</th>
                <th className="px-4 py-3">备注</th>
                <th className="px-4 py-3">接收时间</th>
                <th className="px-4 py-3 text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">加载中…</td></tr>
              )}
              {!loading && filteredRows.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">没有符合条件的邮件回复</td></tr>
              )}
              {!loading && filteredRows.map((r) => (
                <ReplyRow
                  key={r.id}
                  row={r}
                  onAction={onAction}
                  busy={busyId === r.id}
                />
              ))}
            </tbody>
          </table>
        </div>

        <p className="mt-6 text-center text-xs text-slate-500">
          D6.5 规则：只有同工作区的用户才会被自动发布到评论区；其他发件人一律留作「待处理」供人工审阅。
        </p>
      </main>

      <IgnoreDialog
        open={Boolean(ignoreRow)}
        onClose={() => setIgnoreRow(null)}
        onSubmit={submitIgnore}
        busy={busyId === ignoreRow?.id}
      />
    </div>
  );
}
